import { useState, useEffect } from 'react';
import { adminGet } from '../lib/api';

interface Persona {
  id: string;
  name: string;
  tone: string;
  audience: string;
  jargonLevel: string;
  active: boolean;
  promptTemplate?: string;
  version?: string;
  versions?: string[];
}

export function PersonaManager() {
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [selected, setSelected] = useState<Persona | null>(null);
  const [promptText, setPromptText] = useState('');

  useEffect(() => {
    (async () => {
      const resp = await adminGet('/api/admin/personas');
      if (resp.ok && resp.data) setPersonas((resp.data as { personas: Persona[] }).personas || []);
    })();
  }, []);

  async function selectPersona(p: Persona) {
    const resp = await adminGet(`/api/admin/personas/${p.id}`);
    if (resp.ok && resp.data) {
      const detail = resp.data as Persona;
      setSelected(detail);
      setPromptText(detail.promptTemplate || '');
    }
  }

  if (selected) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => setSelected(null)} className="text-dark-400 hover:text-white transition-colors">
              <span className="material-symbols-outlined">arrow_back</span>
            </button>
            <h1 className="text-2xl font-display font-bold text-white">{selected.name}</h1>
            <span className={`px-2 py-0.5 text-xs rounded ${selected.active ? 'bg-severity-low/10 text-severity-low' : 'bg-dark-600 text-dark-400'}`}>
              {selected.active ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div className="flex gap-2">
            <select className="px-3 py-1.5 text-sm bg-dark-700 text-dark-300 rounded-lg border border-dark-650">
              {(selected.versions || ['v1.1.0']).map(v => <option key={v}>{v}</option>)}
            </select>
            <button className="px-4 py-1.5 text-sm bg-rights-500/10 text-rights-500 rounded-lg border border-rights-500/20">Save</button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-dark-700 rounded-2xl border border-dark-650/50 p-6">
            <h3 className="text-white font-medium mb-3">Prompt Template</h3>
            <textarea value={promptText} onChange={e => setPromptText(e.target.value)}
              className="w-full h-96 bg-dark-900 text-dark-200 font-mono text-sm p-4 rounded-lg border border-dark-650 focus:border-rights-500 focus:outline-none resize-none"
              spellCheck={false} />
          </div>
          <div className="space-y-4">
            <div className="bg-dark-700 rounded-2xl border border-dark-650/50 p-6">
              <h3 className="text-white font-medium mb-3">Metadata</h3>
              <div className="space-y-3 text-sm">
                <div><span className="text-dark-400 block text-xs">Tone</span><span className="text-dark-300">{selected.tone}</span></div>
                <div><span className="text-dark-400 block text-xs">Audience</span><span className="text-dark-300">{selected.audience}</span></div>
                <div><span className="text-dark-400 block text-xs">Jargon Level</span><span className="text-dark-300 capitalize">{selected.jargonLevel}</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-display font-bold text-white mb-6">Persona Manager</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {personas.map(p => (
          <button key={p.id} onClick={() => selectPersona(p)}
            className="bg-dark-700 rounded-2xl border border-dark-650/50 p-5 text-left hover:border-rights-500/30 transition-colors">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white font-medium">{p.name}</span>
              <span className={`px-2 py-0.5 text-xs rounded ${p.active ? 'bg-severity-low/10 text-severity-low' : 'bg-dark-600 text-dark-400'}`}>
                {p.active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <p className="text-xs text-dark-400">{p.tone}</p>
            <p className="text-xs text-dark-500 mt-1">Audience: {p.audience}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
